/**
 * ShippingAddressCard Component
 * Card display untuk alamat pengiriman dengan contact person
 */

import { Box, chakra, Flex, Icon, Stack } from '@chakra-ui/react';
import { Button, Dropdown, Text } from '@diamond/shared/ui';
import type { ShipmentDates } from '@diamond/sol-buyer/utils';
import { EditOutlined, LocationOnOutlined } from '@mui/icons-material';
import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

export interface ShippingAddressCardProps {
  address?: string;
  shipToName?: string;
  contactName?: string;
  contactPhone?: string;
  deliveryOption?: string;
  shipmentDates?: ShipmentDates;
  onNotesClick: () => void;
  onEditContact: () => void;
  onDeliveryChange?: (shippingTier: string, deliveryOption: string) => void;
}

const labelTextStyles = {
  fontWeight: 500,
  fontSize: '16px',
  letterSpacing: '0',
};

const valueTextStyles = {
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '0',
};

/**
 * Generate dropdown items dari shipment dates
 */
const generateDropdownItems = (dates: ShipmentDates) => {
  return dates.dates_available.map((dateValue, index) => {
    const label =
      index === 0
        ? 'Pengiriman Segera'
        : `H+${index + 1} Pemesanan • ${dates.dates_available_ui[index]}`;

    return { label, value: dateValue };
  });
};

/**
 * ShippingAddressCard Component
 * Menampilkan alamat lengkap, nama pemesan, nomor HP, waktu pengiriman dalam satu card
 */
export function ShippingAddressCard({
  address,
  shipToName,
  contactName,
  contactPhone,
  deliveryOption,
  shipmentDates,
  onNotesClick,
  onEditContact,
  onDeliveryChange,
}: ShippingAddressCardProps) {
  const {
    register,
    formState: { errors },
    setValue,
    watch,
  } = useFormContext();

  /**
   * Generate dropdown items untuk waktu pengiriman
   */
  const dropdownItems = useMemo(() => {
    if (
      !shipmentDates?.dates_available?.length ||
      !shipmentDates?.dates_available_ui?.length
    ) {
      return [];
    }
    return generateDropdownItems(shipmentDates);
  }, [shipmentDates]);

  // Watch current delivery date and option
  const currentDeliveryDate = watch('delivery_date');
  const currentDeliveryOption = watch('delivery_option');

  /**
   * Initialize default values (first date = Pengiriman Segera)
   * Also handles form reset - re-check if delivery_date becomes empty
   */
  useEffect(() => {
    if (dropdownItems.length === 0) return;

    // Check current delivery date value
    const deliveryDate = currentDeliveryDate;

    // Set default if delivery_date is empty
    // This handles both initial render and form reset scenarios
    if (
      deliveryDate === null ||
      deliveryDate === undefined ||
      deliveryDate === ''
    ) {
      setValue('delivery_date', dropdownItems[0].value);
      setValue('delivery_option', 'immediate');

      // Trigger callback
      if (onDeliveryChange) {
        onDeliveryChange('Pengiriman Segera', 'immediate');
      }
    }
  }, [dropdownItems, setValue, currentDeliveryDate, onDeliveryChange]);

  /**
   * Handle date selection change
   */
  const handleDateChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = event.target.value;
    setValue('delivery_date', selectedValue);

    // Determine delivery option dan shipping tier
    const index =
      shipmentDates?.dates_available.findIndex(
        (date) => date === selectedValue
      ) ?? -1;

    let deliveryOption = 'immediate';
    let shippingTier = 'Pengiriman Segera';

    if (index === 0) {
      deliveryOption = 'immediate';
      shippingTier = 'Pengiriman Segera';
    } else if (index > 0) {
      deliveryOption = `custom_date_${index + 1}`;
      shippingTier = `H+${index + 1}`;
    }

    setValue('delivery_option', deliveryOption);

    // Trigger callback untuk tracking
    if (onDeliveryChange) {
      onDeliveryChange(shippingTier, deliveryOption);
    }
  };

  /**
   * Get delivery info text berdasarkan delivery option
   */
  const getDeliveryInfoText = () => {
    if (currentDeliveryOption === 'immediate') {
      return 'Pembuatan PO dihari Senin - Jumat sebelum jam 15:00 atau Sabtu sebelum jam 12:00 akan dikirim H+1 hari kerja. Diluar jam tersebut, maka pesanan akan diproses dihari berikutnya.';
    }
    return 'Tanggal maksimal 5 hari kerja sejak pembuatan PO. Pengiriman hanya dilakukan pada hari kerja, Tidak ada pengiriman di hari minggu dan tanggal merah.';
  };

  return (
    <Box
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="2xl"
      p={{ base: 4, md: 6 }}
      bg="white"
      boxShadow="sm"
    >
      <Stack spacing={{ base: 5, md: 6 }}>
        {/* Header - Desktop: Row, Mobile: Stack */}
        <Flex
          direction={{ base: 'column', md: 'row' }}
          align={{ base: 'flex-start', md: 'center' }}
          gap={{ base: 2, md: 3 }}
        >
          {/* Opsi Pengiriman */}
          <Flex align="center" gap={2}>
            <Icon as={LocationOnOutlined} color="#0941A8" boxSize={6} />
            <Text
              fontWeight={500}
              fontSize={{ base: '16px', md: '18px' }}
              color="gray.900"
            >
              Opsi Pengiriman
            </Text>
          </Flex>

          {/* Arrow + Warehouse Name */}
          <Flex align="center" gap={2} pl={{ base: '32px', md: 0 }}>
            <Text
              fontSize={{ base: '16px', md: '18px' }}
              color="#0941A8"
              fontWeight={400}
            >
              →
            </Text>
            <Text
              fontWeight={600}
              fontSize={{ base: '16px', md: '18px' }}
              color="#0941A8"
            >
              {shipToName || 'Gudang Jakarta'}
            </Text>
          </Flex>
        </Flex>

        {/* Alamat Lengkap */}
        <Stack spacing={3}>
          <Text {...labelTextStyles} color="gray.900">
            Alamat Lengkap
          </Text>

          {/* Desktop: Row layout, Mobile: Stack layout */}
          <Flex
            direction={{ base: 'column', md: 'row' }}
            justify={{ md: 'space-between' }}
            align={{ md: 'flex-start' }}
            gap={3}
          >
            <Text {...valueTextStyles} color="gray.900" flex={1}>
              {address || '-'}
            </Text>

            <Button
              variant="pillSubtle"
              size="md"
              onClick={onNotesClick}
              leftIcon={<EditOutlined fontSize="small" />}
              flexShrink={0}
              w={{ base: 'full', md: 'auto' }}
            >
              Catatan Pengiriman
            </Button>
          </Flex>
        </Stack>

        {/* Contact Info */}
        <Stack spacing={3}>
          {/* Desktop & Mobile: Values + Button */}
          <Flex
            direction={{ base: 'column', md: 'row' }}
            gap={{ base: 3, md: 6 }}
            align={{ md: 'flex-end' }}
          >
            {/* Values Container */}
            <Flex
              direction={{ base: 'column', md: 'row' }}
              gap={{ base: 3, md: 6 }}
              flex={1}
            >
              {/* Nama Pemesan */}
              <Box flex={1}>
                <Text {...labelTextStyles} color="gray.900" mb={2}>
                  Nama Pemesan
                  {!contactName && (
                    <chakra.span color="red.500" ml={1}>
                      *
                    </chakra.span>
                  )}
                </Text>
                {contactName ? (
                  <Text
                    {...valueTextStyles}
                    color="gray.900"
                    textTransform="uppercase"
                    letterSpacing="0.04em"
                  >
                    {contactName}
                  </Text>
                ) : (
                  <Text {...valueTextStyles} color="red.500" fontStyle="italic">
                    Wajib dilengkapi
                  </Text>
                )}
              </Box>

              {/* Nomor HP */}
              <Box flex={1}>
                <Text {...labelTextStyles} color="gray.900" mb={2}>
                  Nomor Hp{' '}
                  <chakra.span color="gray.500" fontSize="xs">
                    (nomor yang dihubungi jika ada kendala)
                  </chakra.span>
                  {!contactPhone && (
                    <chakra.span color="red.500" ml={1}>
                      *
                    </chakra.span>
                  )}
                </Text>
                {contactPhone ? (
                  <Text {...valueTextStyles} color="gray.900">
                    {contactPhone}
                  </Text>
                ) : (
                  <Text {...valueTextStyles} color="red.500" fontStyle="italic">
                    Wajib dilengkapi
                  </Text>
                )}
              </Box>
            </Flex>

            {/* Button Ubah */}
            <Button
              variant="pillSubtle"
              size="sm"
              leftIcon={<EditOutlined fontSize="small" />}
              onClick={onEditContact}
              flexShrink={0}
              w={{ base: 'full', md: 'auto' }}
            >
              Ubah
            </Button>
          </Flex>
        </Stack>

        {/* Waktu Pengiriman */}
        {dropdownItems.length > 0 && (
          <Stack spacing={3}>
            <Flex justifyContent="space-between" alignItems="center">
              <Flex align="center" gap={1}>
                <Text {...labelTextStyles} color="gray.900">
                  Waktu Pengiriman
                </Text>
                <chakra.span color="red.500" fontSize="sm">
                  *
                </chakra.span>
              </Flex>
              {/* <Icon as={EditOutlined} color="gray.300" boxSize={4} /> */}
            </Flex>

            <Stack spacing={3}>
              <Text fontSize="xs" color="gray.600" lineHeight="1.6">
                {getDeliveryInfoText()}
              </Text>
              <Dropdown
                id="delivery_date"
                name="delivery_date"
                size="lg"
                rounded="lg"
                register={register}
                placeholder="Pilih waktu pengiriman"
                items={dropdownItems}
                errors={errors}
                onChange={handleDateChange}
              />
            </Stack>
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
